import React, { useState } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  CardMedia,
  Tooltip,
  Chip,
  Container,
  InputAdornment,
  Avatar,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  TravelExplore as FetchIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  Visibility as ViewsIcon,
  CalendarToday as DateIcon,
  CheckCircle as SuccessIcon,
  GridView as GridViewIcon,
  ViewModule as ModuleViewIcon,
  List as ListViewIcon,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import axios from "axios";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../../Firebase/Firebase";
import { YOUR_YOUTUBE_API_KEY } from "../../../Assets/Secretkeys/Secretkey";

// Styled components for compact UI
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: "white",
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const CompactCard = styled(Card)(({ theme }) => ({
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: "none",
  fontWeight: "bold",
  padding: "8px 20px",
}));

const AddLink = () => {
  const [links, setLinks] = useState([""]);
  const [videoDataArray, setVideoDataArray] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("grid"); // 'grid', 'compact', 'list'

  const API_KEY = YOUR_YOUTUBE_API_KEY;

  const extractVideoId = (url) => {
    const regex = /(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const parseDuration = (isoDuration) => {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);
    const hours = parseInt(matches[1] || 0);
    const minutes = parseInt(matches[2] || 0);
    const seconds = parseInt(matches[3] || 0);
    return `${hours > 0 ? String(hours).padStart(2, "0") + ":" : ""}${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const fetchAllVideos = async () => {
    setLoading(true);
    setError("");
    const newVideos = [];
    const videoIdsToFetch = links
      .map(extractVideoId)
      .filter((id) => id && !videoDataArray.some((v) => v.videoId === id));

    if (videoIdsToFetch.length === 0) {
      setNotification({
        open: true,
        message: "No new or valid links to fetch.",
        severity: "info",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://www.googleapis.com/youtube/v3/videos?id=${videoIdsToFetch.join(
          ","
        )}&part=snippet,contentDetails,statistics&key=${API_KEY}`
      );

      if (response.data.items) {
        response.data.items.forEach((video) => {
          newVideos.push({
            id: video.id,
            title: video.snippet.title,
            description: video.snippet.description,
            videoId: video.id,
            views: Number(video.statistics.viewCount).toLocaleString(),
            duration: parseDuration(video.contentDetails.duration),
            uploadDate: new Date(
              video.snippet.publishedAt
            ).toLocaleDateString(),
            thumbnail: video.snippet.thumbnails.medium.url,
            channelTitle: video.snippet.channelTitle,
          });
        });
      }
      setVideoDataArray((prev) => [...prev, ...newVideos]);
      setLinks([""]);
    } catch (err) {
      console.error(err);
      const errorMessage =
        err.response?.data?.error?.message ||
        "Failed to fetch video details. Check API Key and links.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToFirebase = async () => {
    setLoading(true);
    try {
      const collectionName = `${new Date().toISOString().split("T")[0]}`;
      await addDoc(collection(db, "linkCollections"), {
        videos: videoDataArray,
        createdAt: new Date(),
        collectionName: collectionName,
      });
      setNotification({
        open: true,
        message: `Successfully saved ${videoDataArray.length} videos to Firebase!`,
        severity: "success",
      });
      setVideoDataArray([]);
    } catch (err) {
      console.error(err);
      setNotification({
        open: true,
        message: "Failed to save to Firebase.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLinkChange = (index, value) => {
    const newLinks = [...links];
    newLinks[index] = value;
    setLinks(newLinks);
  };

  const addNewLinkField = () => setLinks([...links, ""]);

  const removeLinkField = (index) => {
    if (links.length > 1) {
      const newLinks = links.filter((_, i) => i !== index);
      setLinks(newLinks);
    }
  };

  const removeVideoPreview = (videoId) => {
    setVideoDataArray((prev) =>
      prev.filter((video) => video.videoId !== videoId)
    );
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") return;
    setNotification({ ...notification, open: false });
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Grid configuration based on view mode
  const getGridConfig = () => {
    switch (viewMode) {
      case "compact":
        return { xs: 12, sm: 6, md: 4, lg: 3 };
      case "grid":
        return { xs: 12, sm: 6, md: 4 };
      case "list":
        return { xs: 12 };
      default:
        return { xs: 12, sm: 6, md: 4 };
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header Section */}
      <GradientPaper>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{
                width: 50,
                height: 50,
                mr: 2,
                background: "rgba(255,255,255,0.2)",
              }}
            >
              <YouTubeIcon sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                fontWeight="bold"
                gutterBottom
              >
                YouTube Video Manager
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Add and manage YouTube video links
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={<SuccessIcon />}
            label={`${videoDataArray.length} videos`}
            variant="outlined"
            sx={{
              background: "rgba(255,255,255,0.2)",
              color: "white",
              borderColor: "rgba(255,255,255,0.3)",
              fontWeight: "bold",
            }}
          />
        </Box>
      </GradientPaper>

      {/* Input Section */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
          Add YouTube Links
        </Typography>

        {links.map((link, index) => (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", mb: 1.5, gap: 1 }}
          >
            <StyledTextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={`YouTube URL #${index + 1}`}
              value={link}
              onChange={(e) => handleLinkChange(index, e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Remove link">
              <IconButton
                size="small"
                onClick={() => removeLinkField(index)}
                disabled={links.length <= 1}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
          <ActionButton
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addNewLinkField}
            size="small"
          >
            Add Link
          </ActionButton>

          <ActionButton
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <FetchIcon />
              )
            }
            onClick={fetchAllVideos}
            disabled={loading || links.every((link) => link.trim() === "")}
            size="small"
          >
            {loading ? "Fetching..." : "Fetch Videos"}
          </ActionButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 1 }} size="small">
            {error}
          </Alert>
        )}
      </Paper>

      {/* Action Buttons & View Controls */}
      {videoDataArray.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                Preview ({videoDataArray.length})
              </Typography>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                aria-label="view mode"
              >
                <ToggleButton value="compact" aria-label="compact view">
                  <Tooltip title="Compact View">
                    <ModuleViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view">
                  <Tooltip title="Grid View">
                    <GridViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <Tooltip title="List View">
                    <ListViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <ActionButton
              variant="contained"
              color="success"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleSaveToFirebase}
              disabled={loading}
              size="small"
            >
              {loading ? "Saving..." : "Save to Firebase"}
            </ActionButton>
          </Box>
        </Paper>
      )}

      {/* Video Previews Grid */}
      {videoDataArray.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={viewMode === "compact" ? 1.5 : 2}>
            {videoDataArray.map((video) => (
              <Grid item {...getGridConfig()} key={video.videoId}>
                <CompactCard>
                  {/* Thumbnail Section */}
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height={
                        viewMode === "compact"
                          ? 120
                          : viewMode === "grid"
                          ? 140
                          : 100
                      }
                      image={video.thumbnail}
                      alt={video.title}
                      sx={{
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                      }}
                    />

                    {/* Duration Badge */}
                    <Chip
                      label={video.duration}
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        backgroundColor: "rgba(0,0,0,0.8)",
                        color: "white",
                        fontSize: "0.6rem",
                        height: "20px",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />

                    {/* Delete Button */}
                    <Tooltip title="Remove preview">
                      <IconButton
                        size="small"
                        onClick={() => removeVideoPreview(video.videoId)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          backgroundColor: "rgba(255,255,255,0.9)",
                          color: "error.main",
                          width: 24,
                          height: 24,
                          "&:hover": {
                            backgroundColor: "error.main",
                            color: "white",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: viewMode === "compact" ? 1 : 1.5,
                      "&:last-child": { pb: viewMode === "compact" ? 1 : 1.5 },
                    }}
                  >
                    {/* Title - Adjusts based on view mode */}
                    <Typography
                      gutterBottom
                      variant={viewMode === "list" ? "body1" : "body2"}
                      component="div"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: viewMode === "compact" ? 2 : 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        height: viewMode === "compact" ? "2.5rem" : "3.5rem",
                        fontWeight: "bold",
                        lineHeight: 1.3,
                        fontSize: viewMode === "compact" ? "0.8rem" : "0.9rem",
                      }}
                    >
                      {video.title}
                    </Typography>

                    {/* Channel Info - Hidden in compact mode */}
                    {viewMode !== "compact" && (
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight="bold"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        {video.channelTitle}
                      </Typography>
                    )}

                    {/* Stats Row */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                        mt: viewMode === "compact" ? 0.5 : 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <ViewsIcon
                          fontSize="small"
                          color="action"
                          sx={{ fontSize: "14px" }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize="0.7rem"
                        >
                          {video.views}
                        </Typography>
                      </Box>

                      {viewMode !== "compact" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <DateIcon
                            fontSize="small"
                            color="action"
                            sx={{ fontSize: "14px" }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontSize="0.7rem"
                          >
                            {video.uploadDate}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Description - Only shown in list view */}
                    {viewMode === "list" && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.8rem",
                          }}
                        >
                          {video.description}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </CompactCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Empty State */}
      {videoDataArray.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{ p: 8, textAlign: "center", borderRadius: 2 }}
        >
          <YouTubeIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No videos added yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add YouTube links above and click "Fetch Videos" to see previews
          </Typography>
        </Paper>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ borderRadius: 1 }}
          iconMapping={{
            success: <SuccessIcon fontSize="inherit" />,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddLink;
